import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase, TABLES } from '../supabase'
import toast from 'react-hot-toast'

const ImportTemplate = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [templateFile, setTemplateFile] = useState(null)
  const [newEventName, setNewEventName] = useState('')

  const generateJudgeAccessCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let result = ''
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  }

  const handleCreateFromTemplate = async () => {
    if (!templateFile || !newEventName.trim()) {
      toast.error('Please provide event name and select a template file')
      return
    }

    try {
      setLoading(true)
      const fileContent = await templateFile.text()
      
      let templateData
      try {
        templateData = JSON.parse(fileContent)
      } catch (parseError) {
        throw new Error('Invalid JSON file. The file may be corrupted. Please export a new template.')
      }

      // Better validation with helpful error messages
      if (!templateData.version) {
        throw new Error('Invalid file: Missing version information. Please export a new template.')
      }

      if (templateData.type === 'scores_only') {
        throw new Error('This is a scores backup file, not an event template. Please use a template file exported from "Copy Event" button.')
      }

      if (templateData.type !== 'event_template') {
        throw new Error(`Invalid file type: "${templateData.type}". Please use an event template file (exported from Copy Event button).`)
      }

      const { data: newEvent, error: eventError } = await supabase
        .from(TABLES.EVENTS)
        .insert({
          name: newEventName.trim(),
          logo_url: templateData.event.logo_url,
          num_judges: templateData.event.num_judges,
          created_by: user.id,
          status: 'active'
        })
        .select()
        .single()

      if (eventError) throw eventError

      // Create criteria and build ID mapping
      const criteriaIdMap = {}
      for (const criterion of templateData.criteria || []) {
        const { data, error } = await supabase
          .from(TABLES.CRITERIA)
          .insert({
            event_id: newEvent.id,
            name: criterion.name,
            description: criterion.description,
            percentage: criterion.percentage,
            display_order: criterion.display_order || 0
          })
          .select()
          .single()

        if (error) throw error
        criteriaIdMap[criterion.name] = data.id
      }

      // Create candidates and build ID mapping
      const candidateIdMap = {}
      for (const candidate of templateData.candidates || []) {
        const { data, error } = await supabase
          .from(TABLES.CANDIDATES)
          .insert({
            event_id: newEvent.id,
            name: candidate.name,
            representation: candidate.representation,
            candidate_number: candidate.candidate_number
          })
          .select()
          .single()

        if (error) throw error
        candidateIdMap[candidate.candidate_number] = data.id
      }

      const judgesData = (templateData.judges || []).map((judge) => ({
        event_id: newEvent.id,
        name: '', // Judge names not included in templates - will be assigned when judges claim slots
        judge_number: judge.judge_number,
        judge_access_code: generateJudgeAccessCode(),
        completed_criteria: [],
        submitted_special_awards: [],
        status: 'active'
      }))

      const { data: judges } = await supabase.from(TABLES.JUDGES).insert(judgesData).select()

      // Create award categories and build ID mapping
      const categoryIdMap = {}
      for (const category of templateData.specialAwardCategories || []) {
        const { data, error } = await supabase
          .from(TABLES.SPECIAL_AWARD_CATEGORIES)
          .insert({
            event_id: newEvent.id,
            name: category.name,
            description: category.description,
            display_order: category.display_order || 0
          })
          .select()
          .single()

        if (error) throw error
        categoryIdMap[category.name] = data.id
      }

      // Create special awards
      for (const award of templateData.specialAwards || []) {
        const { error } = await supabase
          .from(TABLES.SPECIAL_AWARDS)
          .insert({
            event_id: newEvent.id,
            name: award.name,
            description: award.description,
            award_type: award.award_type,
            category_id: categoryIdMap[award.category_name] || null,
            criteria_id: criteriaIdMap[award.criteria_name] || null,
            overall_ranking_position: award.overall_ranking_position,
            assigned_candidate_id: candidateIdMap[award.assigned_candidate_number] || null,
            display_order: award.display_order || 0,
            add_to_overall: award.add_to_overall || false,
            overall_points: award.overall_points || 0
          })

        if (error) {
          console.error('Error creating award:', award.name, error)
          throw error
        }
      }

      console.log('Import completed:', {
        criteria: templateData.criteria?.length || 0,
        candidates: templateData.candidates?.length || 0,
        judges: judges?.length || 0,
        categories: templateData.specialAwardCategories?.length || 0,
        awards: templateData.specialAwards?.length || 0
      })

      toast.success(`Event "${newEventName}" created from template! (${templateData.criteria?.length || 0} criteria, ${templateData.candidates?.length || 0} candidates, ${templateData.specialAwards?.length || 0} awards)`)
      navigate(`/admin/events/${newEvent.id}`, {
        state: {
          judgeAccessCodes: judges.map(j => ({ 
            judgeName: j.name || `Judge ${j.judge_number}`, 
            accessCode: j.judge_access_code 
          })),
          eventName: newEventName
        }
      })
    } catch (error) {
      console.error('Error:', error)
      toast.error(`Failed to create event: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Event from Template</h1>
            <p className="text-gray-600">Upload a template file from an existing event</p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                New Event Name *
              </label>
              <input
                type="text"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                placeholder="Enter name for your new event"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Template File (.json) *
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setTemplateFile(e.target.files[0])}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  disabled={loading}
                />
              </div>
              {templateFile && (
                <div className="mt-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-teal-800">
                      Selected: <span className="font-medium">{templateFile.name}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-900 mb-1">What will be imported?</p>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>✓ Event structure (criteria, candidates, judges)</li>
                    <li>✓ Special awards and categories (if any)</li>
                    <li>✓ New judge access codes (generated automatically)</li>
                    <li>✗ Scores (not included in templates)</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => navigate('/admin')}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFromTemplate}
                disabled={!templateFile || !newEventName.trim() || loading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </span>
                ) : (
                  'Create Event from Template'
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Don't have a template?{' '}
            <button
              onClick={() => navigate('/create-event')}
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              Create event manually
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}

export default ImportTemplate
